import { Asset, TenantUserSettings } from '@/api/generatedApi';
import { useToast } from '@/hooks/useToast';
import { useSessionState } from '@/hooks/useSessionState';
import { ReactNode } from 'react';
import { CompareCartContext, CompareCartState } from './CompareCartContext';
import { isComparableAsset } from '@/utils/models';

const SESSION_STORAGE_KEY = 'physnaCompareCart';

export const CompareCartProvider = ({ children }: { children: ReactNode }) => {
  const { showToast } = useToast();

  const [selectedForCompare, setSelectedForCompareState] = useSessionState<{ asset?: Asset }>(SESSION_STORAGE_KEY, {});

  const setSelectedForCompare = (selection: { asset?: Asset }) => {
    if (selection.asset && !isComparableAsset(selection.asset)) {
      showToast({
        message: 'Only model or text file assets can be selected for compare',
        type: 'warning',
      });
      return;
    }
    setSelectedForCompareState(selection);
  };

  const isSelected = (assetId: string) => {
    return selectedForCompare.asset?.id === assetId;
  };

  const clearCart = () => {
    setSelectedForCompareState({});
  };

  const getCompareUrl = (asset1: Asset, asset2: Asset, userSettings?: TenantUserSettings[]) => {
    const compareParameters = new URLSearchParams({
      asset1Id: asset1.id,
      asset2Id: asset2.id,
      tenant1Id: asset1.tenantId,
      tenant2Id: asset2.tenantId,
    });

    const tenant1ShortName =
      userSettings?.find(tenant => tenant.tenantId === asset1.tenantId && tenant.userEnabled)?.tenantShortName ||
      asset1.tenantId;

    return `/tenants/${tenant1ShortName}/compare?${compareParameters}`;
  };

  const value: CompareCartState = {
    selectedForCompare,
    setSelectedForCompare,
    isSelected,
    clearCart,
    getCompareUrl,
  };

  return <CompareCartContext.Provider value={value}>{children}</CompareCartContext.Provider>;
};
